import { ReactComponent as AccountIcon } from '../../assets/icons/account-page-icon.svg';
import { ReactComponent as HomeIcon } from '../../assets/icons/home.svg';
import { ReactComponent as SessionsIcon } from '../../assets/icons/sessions-page-icon.svg';
import { ReactComponent as StudentsIcon } from '../../assets/icons/students-page-icon.svg';
import { ReactComponent as SubscriptionsIcon } from '../../assets/icons/subscriptions-page-icon.svg';
import { ReactComponent as SupportIcon } from '../../assets/icons/support-page-icon.svg';
import { ReactComponent as VouchersIcon } from '../../assets/icons/vouchers-page-icon.svg';
import { PATH_HUB_BASE } from '../legacy/pagePath';
import type { PageHeaderIcon } from './usePageHeader';

// Per-page header config, ported from student-hub's common/page.ts. Drives the
// app-shell header's icon + breadcrumb. Only pages listed here render a header;
// everything else falls back to usePageTitle (see AppShell PageHeader).
export interface PageConfig {
  /** Page key, matches the parent segment parsed from the path. */
  name: string;
  icon: PageHeaderIcon;
  /** Breadcrumb / header label. */
  label: string;
  showIcon?: boolean;
  showBackButton?: boolean;
}

// Keyed by the parent path segment (see parseCurrentPage). Start with Home only;
// add entries here as each page is migrated to the new header.
export const pageConfigs: Record<string, PageConfig> = {
  home: {
    name: 'home',
    icon: HomeIcon,
    label: 'Home',
    showIcon: true,
  },
  students: {
    name: 'students',
    icon: StudentsIcon,
    label: 'Students',
    showIcon: true,
  },
  sessions: {
    name: 'sessions',
    icon: SessionsIcon,
    label: 'Sessions',
    showIcon: true,
  },
  vouchers: {
    name: 'vouchers',
    icon: VouchersIcon,
    label: 'Cluey Vouchers',
    showIcon: true,
  },
  account: {
    name: 'account',
    icon: AccountIcon,
    label: 'Account',
    showIcon: true,
  },
  'manage-service': {
    name: 'manage-service',
    icon: SupportIcon,
    label: 'Support',
    showIcon: true,
  },
  subscriptions: {
    name: 'subscriptions',
    icon: SubscriptionsIcon,
    label: 'Subscriptions',
    showIcon: true,
  },
};

/**
 * Returns the header config for a page name, or undefined if it has none yet.
 *
 * @param {string} pageName - The parent page segment (e.g. 'home').
 * @returns {PageConfig | undefined} The page's header config, or undefined.
 */
export function getPageConfig(pageName: string): PageConfig | undefined {
  return pageConfigs[pageName];
}

export interface CurrentPageInfo {
  /** The parent/folder segment (e.g. 'home', 'sessions'). */
  parent: string;
  /** The child segment if in a subdirectory, otherwise null. */
  child: string | null;
}

/**
 * Parses a hub pathname into parent/child page info. Strips the
 * `/portal/customer/` base and surrounding slashes before splitting.
 *
 * @param {string} pathname - The current route pathname.
 * @returns {CurrentPageInfo} The parsed parent/child page segments.
 *
 * @example
 * // '/portal/customer/home/'           -> { parent: 'home', child: null }
 * // '/portal/customer/sessions/'       -> { parent: 'sessions', child: null }
 * // '/portal/customer/account/details' -> { parent: 'account', child: 'details' }
 */
export function parseCurrentPage(pathname: string): CurrentPageInfo {
  const clean = pathname.replace(PATH_HUB_BASE, '').replace(/^\/+|\/+$/g, '');

  if (!clean) return { parent: 'home', child: null };

  const [parent, child] = clean.split('/');
  return { parent, child: child || null };
}
